import { useEffect, useRef, useState } from "react";
import { Animated, LayoutChangeEvent, StyleSheet, View } from "react-native";
import BMView from "../View/BMView";
import Banner, { BannerProps } from "./Banner";

type Props = {
  banners: Omit<BannerProps, "onClose">[];
  onCloseBanner?: (key: string) => void;
  delay?: number;
};

export default function BannerContainer({
  banners: initialBanners,
  onCloseBanner,
  delay = 8000,
}: Props) {
  const [banners, setBanners] = useState(initialBanners);
  const [current, setCurrent] = useState(0);
  const [next, setNext] = useState(1);
  const [bannerHeight, setBannerHeight] = useState(0);

  const measure = useRef(false);
  const translateY = useRef(new Animated.Value(0));
  const containerHeightAnim = useRef(new Animated.Value(0));

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Measure the height of the banner container
  function onLayout(e: LayoutChangeEvent) {
    const height = e.nativeEvent.layout.height;
    if (height !== bannerHeight) {
      setBannerHeight(height);
      measure.current = true;
    }
  }

  // Start the interval to animate the banners
  function startInterval() {
    if (banners.length <= 1) return;
    clearExistingInterval();
    intervalRef.current = setInterval(() => {
      animateSlideDown();
    }, delay);
  }

  // Clear the existing interval if it exists
  function clearExistingInterval() {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }

  // Animate the slide down effect
  function animateSlideDown() {
    Animated.timing(translateY.current, {
      toValue: bannerHeight,
      duration: 300,
      useNativeDriver: true,
    }).start(() => {
      setNext((prev) => {
        setCurrent(prev);
        const index = (prev + 1) % banners.length;
        return index;
      });
      translateY.current.setValue(0);
    });
  }

  // Animate the close and remove effect for a banner
  function animateCloseAndRemove(index: number) {
    clearExistingInterval();

    const id = banners[index].id;
    if (typeof id === "string") {
      onCloseBanner?.(id);
    }

    // If only one banner is left, animate the height to zero
    if (banners.length <= 1) {
      Animated.timing(containerHeightAnim.current, {
        toValue: 0,
        duration: 400,
        useNativeDriver: false,
      }).start();
      return;
    }

    // Animate the banner sliding up and removing it
    Animated.timing(translateY.current, {
      toValue: -bannerHeight,
      duration: 400,
      useNativeDriver: true,
    }).start(() => {
      setBanners((prev) => {
        const updated = prev.filter((_, i) => i !== index);
        const current = (index - 1 + updated.length) % updated.length;
        setCurrent(current);
        setNext((current + 1) % updated.length);

        return updated;
      });
      translateY.current.setValue(0);
      startInterval();
    });
  }

  // Effect to handle the initial setup and cleanup of the interval
  useEffect(() => {
    if (!measure.current || banners.length <= 1) return;
    startInterval();
    return clearExistingInterval;
  }, [measure, bannerHeight, banners.length]);

  useEffect(() => {
    Animated.timing(containerHeightAnim.current, {
      toValue: bannerHeight,
      duration: 400,
      useNativeDriver: false,
    }).start();
    return;
  }, [bannerHeight])

  return (
    <BMView>
      <Animated.View
        style={[{ height: containerHeightAnim.current }, styles.wrapper]}
      >
        <Animated.View
          style={[
            styles.container,
            {
              top: -bannerHeight,
              transform: [{ translateY: translateY.current }],
            },
          ]}
        >
          <Banner {...banners[next % banners.length]} />
          <View onLayout={onLayout}>
            <Banner
              {...banners[current % banners.length]}
              onClose={() => animateCloseAndRemove(current)}
            />
          </View>
          <Banner
            {...banners[(current - 1 + banners.length) % banners.length]}
          />
        </Animated.View>
      </Animated.View>
    </BMView>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    overflow: "hidden",
  },
  container: {
    flex: 1,
    width: "100%",
  },
});
